import { ElevatorService } from "../flatbuffers/generated/service/elevator-service";
import { EscalatorService } from "../flatbuffers/generated/service/escalator-service";
import { DeviceServiceContent } from "../flatbuffers/generated/service/device-service-content";

export interface ServiceModeDisplay {
  displayName: string;
  abbreviation: string;
  color: string;
}

interface ServiceLabel {
  displayName: string;
  abbreviation: string;
}

// ---------------------------------------------------------------------------
// Ant Design Tag preset colors, excluding red/green and their variants
// to avoid implying good/bad semantics.
// ---------------------------------------------------------------------------
const COLOR_PALETTE: readonly string[] = [
  "blue",
  "purple",
  "cyan",
  "magenta",
  "orange",
  "volcano",
  "geekblue",
  "gold",
  "lime",
];

/**
 * Deterministic color assignment for a service mode.
 * Uses a simple hash of the content-type + code so the same service mode
 * always maps to the same palette color, without hardcoding any particular
 * service to any particular color.
 */
export function serviceColor(
  contentType: DeviceServiceContent,
  code: ElevatorService | EscalatorService
): string {
  // Mix content type into the hash so elevator code 0 and escalator code 0
  // don't necessarily land on the same color.
  const hash = contentType * 257 + code;
  return COLOR_PALETTE[
    ((hash % COLOR_PALETTE.length) + COLOR_PALETTE.length) %
      COLOR_PALETTE.length
  ];
}

// ---------------------------------------------------------------------------
// Human-readable labels — names and abbreviations only, no color.
// ---------------------------------------------------------------------------
const ELEVATOR_SERVICE_LABELS: Partial<Record<ElevatorService, ServiceLabel>> =
  {
    [ElevatorService.Auto]: { displayName: "Automatic", abbreviation: "AUTO" },
    [ElevatorService.Norm]: { displayName: "Normal", abbreviation: "NORM" },
    [ElevatorService.Run]: { displayName: "Run", abbreviation: "RUN" },
    [ElevatorService.Oos]: {
      displayName: "Out of Service",
      abbreviation: "OOS",
    },
    [ElevatorService.Ins]: { displayName: "Inspection", abbreviation: "INS" },
    [ElevatorService.InsCons]: {
      displayName: "Inspection Console",
      abbreviation: "INSC",
    },
    [ElevatorService.CtIns]: {
      displayName: "CT Inspection",
      abbreviation: "CINS",
    },
    [ElevatorService.CpIns]: {
      displayName: "CP Inspection",
      abbreviation: "CPIS",
    },
    [ElevatorService.HaIns]: {
      displayName: "HA Inspection",
      abbreviation: "HINS",
    },
    [ElevatorService.MrIns]: {
      displayName: "MR Inspection",
      abbreviation: "MINS",
    },
    [ElevatorService.MOS]: { displayName: "MOS", abbreviation: "MOS" },
    [ElevatorService.Ind]: { displayName: "Independent", abbreviation: "IND" },
    [ElevatorService.Test]: { displayName: "Test", abbreviation: "TEST" },
    [ElevatorService.FireMain]: {
      displayName: "Fire Phase 1",
      abbreviation: "FIRE",
    },
    [ElevatorService.FireAlt]: {
      displayName: "Fire Alternate",
      abbreviation: "FALT",
    },
    [ElevatorService.FirePhase1]: {
      displayName: "Fire Phase 1",
      abbreviation: "FPH1",
    },
    [ElevatorService.FirePhase2]: {
      displayName: "Fire Phase 2",
      abbreviation: "FPH2",
    },
    [ElevatorService.HospitalPhase1]: {
      displayName: "Hospital Phase 1",
      abbreviation: "HPH1",
    },
    [ElevatorService.HospitalPhase2]: {
      displayName: "Hospital Phase 2",
      abbreviation: "HPH2",
    },
    [ElevatorService.Shuttle]: { displayName: "Shuttle", abbreviation: "SHUT" },
    [ElevatorService.Sabbath]: { displayName: "Sabbath", abbreviation: "SABB" },
    [ElevatorService.Construction]: {
      displayName: "Construction",
      abbreviation: "CONS",
    },
    [ElevatorService.Earthquake]: {
      displayName: "Earthquake",
      abbreviation: "EQKE",
    },
    [ElevatorService.Attendant]: {
      displayName: "Attendant",
      abbreviation: "ATND",
    },
    [ElevatorService.CodeBlue]: {
      displayName: "Code Blue",
      abbreviation: "CBLU",
    },
    [ElevatorService.Homing]: { displayName: "Homing", abbreviation: "HOME" },
    [ElevatorService.Stalled]: { displayName: "Stalled", abbreviation: "STLD" },
    [ElevatorService.CarToLobby]: {
      displayName: "Car to Lobby",
      abbreviation: "CLBY",
    },
    [ElevatorService.LoadBypass]: {
      displayName: "Load Bypass",
      abbreviation: "LBYP",
    },
    [ElevatorService.MedicalEmergency]: {
      displayName: "Medical Emergency",
      abbreviation: "MEDC",
    },
    [ElevatorService.VIP]: { displayName: "VIP", abbreviation: "VIP" },
    [ElevatorService.SecurityRecall]: {
      displayName: "Security Recall",
      abbreviation: "SREC",
    },
    [ElevatorService.EmergencyPower]: {
      displayName: "Emergency Power",
      abbreviation: "EPWR",
    },
    [ElevatorService.Parked]: { displayName: "Parked", abbreviation: "PARK" },
    [ElevatorService.Idle]: { displayName: "Idle", abbreviation: "IDLE" },
    [ElevatorService.Initializing]: {
      displayName: "Initializing",
      abbreviation: "INIT",
    },
    [ElevatorService.EmergencyStopButton]: {
      displayName: "Emergency Stop",
      abbreviation: "ESTP",
    },
    [ElevatorService.CarEmergency]: {
      displayName: "Car Emergency",
      abbreviation: "CEMG",
    },
    [ElevatorService.EmergencyRecall]: {
      displayName: "Emergency Recall",
      abbreviation: "EREC",
    },
    [ElevatorService.Night]: { displayName: "Night", abbreviation: "NITE" },
    [ElevatorService.Unknown]: { displayName: "Unknown", abbreviation: "UNK" },
  };

const ESCALATOR_SERVICE_LABELS: Partial<
  Record<EscalatorService, ServiceLabel>
> = {
  [EscalatorService.Auto]: { displayName: "Automatic", abbreviation: "AUTO" },
  [EscalatorService.MOS]: { displayName: "MOS", abbreviation: "MOS" },
  [EscalatorService.Inspection]: {
    displayName: "Inspection",
    abbreviation: "INS",
  },
  [EscalatorService.Construction]: {
    displayName: "Construction",
    abbreviation: "CONS",
  },
  [EscalatorService.Run]: { displayName: "Run", abbreviation: "RUN" },
  [EscalatorService.Stopped]: { displayName: "Stopped", abbreviation: "STOP" },
  [EscalatorService.Normal]: { displayName: "Normal", abbreviation: "NORM" },
};

/**
 * Split a PascalCase enum name into separate words.
 * "FrontDoorNudging" → "Front Door Nudging"
 * Handles consecutive capitals like "ARD" or "EPC" as single tokens.
 */
function splitPascalCase(name: string): string {
  return name
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2");
}

/**
 * Build a short abbreviation from a PascalCase name.
 * Takes the first letter of each word, uppercase, max 4 chars.
 * "FrontDoorNudging" → "FDN", "Security" → "SEC" (first 3 of single word)
 */
function abbreviate(name: string): string {
  const words = splitPascalCase(name).split(" ");
  if (words.length === 1) {
    return words[0].substring(0, 4).toUpperCase();
  }
  return words
    .map((w) => w[0])
    .join("")
    .substring(0, 4)
    .toUpperCase();
}

function fallbackLabel(code: ElevatorService | EscalatorService): ServiceLabel {
  const enumName =
    ElevatorService[code as ElevatorService] ??
    EscalatorService[code as EscalatorService];
  if (enumName && typeof enumName === "string") {
    return {
      displayName: splitPascalCase(enumName),
      abbreviation: abbreviate(enumName),
    };
  }
  return {
    displayName: `Service ${code}`,
    abbreviation: `S${code}`,
  };
}

export function getServiceModeDisplay(
  serviceContent: DeviceServiceContent | null | undefined,
  code: ElevatorService | EscalatorService | null | undefined
): ServiceModeDisplay | null {
  if (serviceContent == null || code == null) return null;

  let label: ServiceLabel | undefined;

  if (serviceContent === DeviceServiceContent.ElevatorServiceMessage) {
    label = ELEVATOR_SERVICE_LABELS[code as ElevatorService];
  } else if (serviceContent === DeviceServiceContent.EscalatorServiceMessage) {
    label = ESCALATOR_SERVICE_LABELS[code as EscalatorService];
  } else {
    return null;
  }

  const resolved = label ?? fallbackLabel(code);
  return {
    ...resolved,
    color: serviceColor(serviceContent, code),
  };
}
